import {Button, notification, Tooltip} from "antd";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {v4} from "uuid";
import EmptyLine from "../../app-components/outputs/EmptyLine";
import CheckCSName from "../../app-components/outputs/CheckCSName";
import {useAppContext} from "../../context/AppContext";
import ComparisonCSName from "../../app-components/outputs/ComparisonCSName";
import React from 'react';
import {CopyOutlined} from "@ant-design/icons";

const DEBUG = true;
const dlog = (...args) => { if (DEBUG) console.log('[ResultTable]', ...args); };

const DESC_CELL_STYLE = {
    border: '1px solid #e0e0e0',
    paddingLeft: '4px',
    paddingRight: '4px',
    whiteSpace: 'nowrap',
    wordWrap: 'break-word',
    verticalAlign: 'top',
    color: '#444',
};

const DESC_HEADER_STYLE = {
    border: '1px solid #e0e0e0',
    backgroundColor: '#f0f2f5',
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontWeight: 'bold',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const applyStyles = (el, styles) => {
    for (const key in styles) {
        el.style[key] = styles[key];
    }
};

const injectDescriptionDOM = (elTable, descMap) => {
    if (!elTable || !descMap || Object.keys(descMap).length === 0) {
        dlog('skip: no table or empty descMap');
        return;
    }

    // Defensive cleanup of any stale injections from prior runs
    elTable.querySelectorAll('[data-desc-injected="true"]').forEach(el => el.remove());

    const tbody = elTable.tBodies && elTable.tBodies[0];
    if (!tbody || tbody.rows.length === 0) {
        dlog('skip: no <tbody> rows in DOM', {tbodyExists: !!tbody, rowCount: tbody?.rows?.length});
        return;
    }

    const tbodyRows = Array.from(tbody.rows);

    const candidates = [];
    const voteCount = {};
    const rowTextSamples = [];

    for (const row of tbodyRows) {
        const cells = Array.from(row.cells);
        const rowCands = [];
        const cellTexts = [];
        for (let cIdx = 0; cIdx < cells.length; cIdx++) {
            const text = (cells[cIdx].textContent || '').trim();
            cellTexts.push(text);
            if (text && Object.prototype.hasOwnProperty.call(descMap, text)) {
                const rowSpan = cells[cIdx].rowSpan || 1;
                rowCands.push({cellIdx: cIdx, rowSpan, descText: descMap[text]});
                voteCount[cIdx] = (voteCount[cIdx] || 0) + 1;
            }
        }
        candidates.push(rowCands);
        rowTextSamples.push(cellTexts);
    }

    dlog('rows scanned (DOM)', {
        rowCount: tbodyRows.length,
        descMapSampleKeys: Object.keys(descMap).slice(0, 10),
        firstThreeRowsCellTexts: rowTextSamples.slice(0, 3),
        voteCount,
        candidatesPerRow: candidates.map(c => c.length),
    });

    const indices = Object.keys(voteCount).map(Number).sort((a, b) => a - b);
    if (indices.length === 0) {
        dlog('skip: no cells matched any description key');
        return;
    }

    let winnerIdx = indices[0];
    for (const idx of indices) {
        if (voteCount[idx] > voteCount[winnerIdx]) winnerIdx = idx;
    }

    if (tbodyRows.length >= 2 && voteCount[winnerIdx] < 2) {
        dlog('skip: multi-row table but winning column has only 1 vote', {winnerIdx, votes: voteCount[winnerIdx]});
        return;
    }

    dlog('INJECT', {winnerIdx, winnerVotes: voteCount[winnerIdx], rowCount: tbodyRows.length});

    // Inject thead
    const thead = elTable.tHead;
    if (thead && thead.rows.length > 0) {
        const firstHeadRow = thead.rows[0];
        const th = document.createElement('th');
        th.textContent = 'Description';
        th.setAttribute('data-no-copy', 'true');
        th.setAttribute('data-desc-injected', 'true');
        applyStyles(th, DESC_HEADER_STYLE);
        if (thead.rows.length > 1) th.rowSpan = thead.rows.length;
        firstHeadRow.appendChild(th);
    }

    // Inject tbody cells (respecting rowSpan)
    let pendingRowSpan = 0;
    for (let rIdx = 0; rIdx < tbodyRows.length; rIdx++) {
        if (pendingRowSpan > 0) {
            pendingRowSpan -= 1;
            continue;
        }
        const winning = candidates[rIdx].find(c => c.cellIdx === winnerIdx);
        const td = document.createElement('td');
        td.setAttribute('data-no-copy', 'true');
        td.setAttribute('data-desc-injected', 'true');
        applyStyles(td, DESC_CELL_STYLE);
        if (winning) {
            td.textContent = winning.descText;
            if (winning.rowSpan > 1) td.rowSpan = winning.rowSpan;
            pendingRowSpan = winning.rowSpan - 1;
        }
        tbodyRows[rIdx].appendChild(td);
    }
};

export default function ResultTable({children, style = {}, footer, maxWidth, hideToolbar, enableReactRender, data, scrollable, ...props}) {
    const id = v4();
    const fontFamily = window.location.pathname.includes('/app/report') ? 'Calibri' : 'Segoe ui'
    const fontSize = window.location.pathname.includes('/app/report') ? '11pt' : '9pt'

    const {getSelectedHistory, getDescriptionMap} = useAppContext()

    const [hover, setHover] = useState(false)
    const containerRef = useRef(null);

    const descMap = getDescriptionMap ? getDescriptionMap() : {};

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const elTable = container.getElementsByTagName('table')[0];
        injectDescriptionDOM(elTable, descMap);
    });

    const copy = async () => {
        const container = containerRef.current;
        const elTable = container?.getElementsByTagName('table')[0];
        dlog('copy clicked', {hasContainer: !!container, hasTable: !!elTable});

        if (!elTable) {
            notification.error({message: 'Failed to copy table (no table found).'});
            return;
        }

        try {
            const clone = elTable.cloneNode(true);

            // Word, tablo seviyesindeki font'u hücre metnine güvenilir biçimde
            // miras ettirmiyor; bu yüzden her hücrenin sayfada GÖRÜNEN (computed)
            // font-family ve font-size'ını klon hücrelere açıkça yaz. Klon, orijinalin
            // birebir kopyası olduğundan hücre sırası aynıdır (strip'ten ÖNCE eşle).
            clone.style.fontFamily = fontFamily;
            clone.style.fontSize = fontSize;
            const origCells = elTable.querySelectorAll('th, td');
            const cloneCells = clone.querySelectorAll('th, td');
            for (let i = 0; i < cloneCells.length; i++) {
                if (!origCells[i]) continue;
                const cs = window.getComputedStyle(origCells[i]);
                cloneCells[i].style.fontFamily = cs.fontFamily;
                cloneCells[i].style.fontSize = cs.fontSize;

                // Text alignment: 'start'/'end' -> 'left'/'right' (Word bunları
                // anlamaz). Hem CSS text-align hem de Word'ün güvenilir biçimde
                // onurlandırdığı eski 'align' attribute'unu yaz.
                let ta = cs.textAlign;
                if (ta === 'start') ta = 'left';
                else if (ta === 'end') ta = 'right';
                if (ta === 'left' || ta === 'right' || ta === 'center' || ta === 'justify') {
                    cloneCells[i].style.textAlign = ta;
                    cloneCells[i].setAttribute('align', ta);
                }
            }

            // Sütun genişlikleri: Word, hücredeki CSS width'i çoğu zaman yok sayar;
            // <colgroup> col genişliklerini ise güvenilir uygular. Tam kolon setine
            // sahip (colspan'sız) bir satırı baz al, kopyaya dahil edilmeyen
            // Description sütununu atla, computed genişliklerden colgroup kur.
            let widthCells = null;
            for (const r of Array.from(elTable.rows)) {
                const cells = Array.from(r.cells);
                const noColspan = cells.every(c => (c.colSpan || 1) === 1);
                const copyCells = cells.filter(c => c.getAttribute('data-no-copy') !== 'true');
                if (noColspan && (!widthCells || copyCells.length > widthCells.length)) {
                    widthCells = copyCells;
                }
            }
            if (widthCells && widthCells.length > 0) {
                const colgroup = document.createElement('colgroup');
                let total = 0;
                for (const c of widthCells) {
                    const col = document.createElement('col');
                    const w = Math.round(parseFloat(window.getComputedStyle(c).width)) || 0;
                    if (w) { col.setAttribute('width', String(w)); total += w; }
                    colgroup.appendChild(col);
                }
                clone.querySelectorAll('colgroup').forEach(cg => cg.remove());
                clone.insertBefore(colgroup, clone.firstChild);
                if (total) clone.setAttribute('width', String(total));
            }

            const stripped = clone.querySelectorAll('[data-no-copy="true"]');
            stripped.forEach(el => el.remove());
            dlog('copy: stripped no-copy nodes', {count: stripped.length});

            const html = clone.outerHTML;
            const plain = clone.innerText || clone.textContent || '';
            dlog('copy: writing to clipboard', {htmlLen: html.length, plainLen: plain.length});

            if (window.ClipboardItem && navigator.clipboard?.write) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([html], {type: 'text/html'}),
                        'text/plain': new Blob([plain], {type: 'text/plain'}),
                    })
                ]);
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(plain);
            } else {
                throw new Error('Clipboard API unavailable');
            }

            notification.success({message: 'Table copied to clipboard!'});
        } catch (e) {
            console.error('[ResultTable] Failed to copy table:', e);
            notification.error({message: `Failed to copy table: ${e?.message || e}`});
        }
    }

    const getColumnCount = () => {
        try {
            // children[0] genelde thead olur, onun içindeki ilk satırın th/td sayısına bakıyoruz
            const head = React.Children.toArray(children).find(
                child => child.type === 'thead' || child.type === 'tbody'
            );

            if (head && head.props.children) {
                const firstRow = React.Children.toArray(head.props.children)[0];
                if (firstRow && firstRow.props.children) {
                    return React.Children.toArray(firstRow.props.children).length;
                }
            }
        } catch (e) {
            console.warn("Sütun sayısı hesaplanamadı, varsayılan değer kullanılıyor.");
        }
        return 1; // Fallback
    };

    const columnCount = getColumnCount();

    const _table = (
        <table
            {...props}
            style={{ fontFamily: fontFamily, fontSize: fontSize, ...style }}
        >
            {children}
            {footer && (
                <tfoot>
                <tr>
                    <td colSpan={columnCount}>
                        {footer}
                    </td>
                </tr>
                </tfoot>
            )}
        </table>
    );

    return (
        <div
            ref={containerRef}
            style={{ display: 'flex', alignItems: 'end', gap: 4 }}
            id={id}
        >
            <div style={{ maxWidth: maxWidth}}>
                {data?.length > 0 && (
                    <div
                        style={{
                            fontFamily: fontFamily,
                            fontSize: fontSize,
                        }}
                    >
                        <EmptyLine/>
                        {(getSelectedHistory()?.type === 'Check' || !getSelectedHistory()?.type) && (
                            <CheckCSName csNameList={data}/>
                        )}
                        {getSelectedHistory()?.type ===
                            'Comparison' && (
                                <ComparisonCSName
                                    csNameList={data}
                                />
                            )}
                        <EmptyLine/>
                    </div>
                )}
                <div
                    style={{position: 'relative'}}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    {hover && (
                        <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={copy}
                            style={{
                                position: 'absolute',
                                top: 2,
                                left: 2,
                                zIndex: 10,
                                opacity: 0.85
                            }}
                        />
                    )}
                    {scrollable && (
                        <div style={{overflowY: 'auto', height: scrollable?.height || 500}}>
                            {_table}
                        </div>
                    )}
                    {
                        !scrollable && (_table)
                    }
                </div>
            </div>
        </div>
    );
}
